import wrapWithFormProvider, { wrapWithReduxFormProvider } from './wrapWithFormProvider';

import type { ErrorsOptions } from './wrapInputWithErrors';
import type { InputActionOptions } from './wrapInputWithActions';
import type { ValidationOptions } from './wrapInputWithValidations';
import { flow } from 'lodash';
import wrapInputWithActions from './wrapInputWithActions';
import wrapInputWithErrors from './wrapInputWithErrors';
import wrapInputWithValidations from './wrapInputWithValidations';

export type InputOptions = InputActionOptions & ValidationOptions & ErrorsOptions;

export const wrapInputBase = (formProviderWrapper, options: InputOptions) => (Component) => {
  return flow(
    wrapInputWithValidations(options),
    wrapInputWithActions(options),
    wrapInputWithErrors(options),
    formProviderWrapper
  )(Component);
};

/**
 * Wraps the component with all the necessary behavior to work within a specific redux form.
 * Only use if you cannot have the inputs exist within a Form component.
 * @param formId - The id of the form the input will belong too.
 * @param formSelector - The selector that pulls the form from state.
 * @param options - Options to tailor behavior of the input within the form.
 */
export const reduxWrapInput = (formId, formSelector, options: InputOptions) => (Component) => {
  return wrapInputBase(wrapWithReduxFormProvider(formId, formSelector), options)(Component);
};

/**
 * Wraps the component with all the necessary behavior to work within a form.
 * The input will connect to the form that it is placed under.
 * @param options - Options to tailor behavior of the input within the form.
 */
const wrapInput = (options: InputOptions) => (Component) => {
  return wrapInputBase(wrapWithFormProvider, options)(Component);
};

export default wrapInput;

/**
 * A set of default options that work with the native <input> tag.
 */
export const inputOptions = {
  inputIdKey: 'name',
  valueKey: 'value',
  labelKey: 'label',
  defaultValidateOn: ['change'],
  defaultIncludeHasErrors: false,
  defaultIncludeErrorMessages: false,
  events: {
    onChange: {
      eventType: 'change'
    },
    onFocus: {
      eventType: 'focus'
    },
    onBlur: {
      eventType: 'blur'
    }
  }
};
